<script setup lang="ts">
import type { SelectGroupOption, SelectOption } from 'naive-ui'
import type { AuthorizationAction } from '~/types/authorization.type'
import type { CalendarEventInput } from '~/types/calendar.type'
import type { Project } from '~/types/project.type'
import type { Task } from '~/types/task.type'
import type { TaskList } from '~/types/taskList.type'
import type { TimeEntry } from '~/types/timeEntry.type'
import type { User } from '~/types/user.type'
import { TimeEntryDeleteModal } from '#components'
import { subject } from '@casl/ability'
import { NAvatar } from 'naive-ui'
import { unknownError } from '~/constants'
import { timeEntriesEventsSourceId, timeEntryCloneEventId } from '~/types/calendar.type'

const props = defineProps({
  projectId: {
    type: Number as PropType<Project['id']>,
    default: undefined,
  },
  taskId: {
    type: Number as PropType<Task['id']>,
    default: undefined,
  },
  startAt: {
    type: Date,
    default: new Date(),
  },
  endAt: {
    type: Date,
    default: undefined,
  },
  timeEntry: {
    type: Object as PropType<TimeEntry>,
    default: undefined,
  },
  taskLists: {
    type: Array as PropType<TaskList<'withTasks'>[]>,
    default: () => [],
  },
  projects: {
    type: Array as PropType<Project<'withLeads' | 'withClient'>[]>,
    default: () => [],
  },
  users: {
    type: Array as PropType<Array<User>>,
    default: () => [],
  },
  notes: {
    type: String,
    default: undefined,
  },
  title: {
    type: String,
    default: undefined,
  },
})

const emit = defineEmits<{ close: [boolean] }>()

const lastManualTimeEntry = useLastManualTimeEntry()

const overlay = useOverlay()
const toast = useToast()
const loadingBar = useNaiveLoadingBar()
const { activeCalendar } = storeToRefs(useTimeTracker())
const { user } = storeToRefs(useUserStore())
const { timeEntriesFilter } = storeToRefs(useTimeEntries())

if (activeCalendar.value?.getApi())
  activeCalendar.value?.getApi().getEventById(timeEntryCloneEventId.toString())?.remove()

/* Start of form */

interface TimeEntryModalForm {
  projectId: Project['id'] | null
  userId: ApiTimeEntryCreateParams['userId'] | null
  taskId: ApiTimeEntryCreateParams['taskId'] | null
  notes: ApiTimeEntryCreateParams['notes']
  startAt: number
  endAt: number
  isBilled: ApiTimeEntryCreateParams['isBilled']
}

// TODO: remove once projects and tasks can no longer be deleted when having time entries
let initialProjectId = null
if (props.projectId)
  initialProjectId = props.projectId
else if (lastManualTimeEntry.value && props.projects.some(project => project.id === lastManualTimeEntry.value.projectId))
  initialProjectId = lastManualTimeEntry.value.projectId

let initialTaskId = null
if (props.taskId)
  initialTaskId = props.taskId
else if (lastManualTimeEntry.value && props.taskLists.some(({ tasks }) => tasks.some(task => task.id === lastManualTimeEntry.value.taskId)))
  initialTaskId = lastManualTimeEntry.value.taskId

const model = ref<TimeEntryModalForm>(props.timeEntry
  ? {
      projectId: props.timeEntry.projectId,
      userId: props.timeEntry.userId,
      taskId: props.timeEntry.taskId,
      startAt: props.timeEntry.startAt.getTime(),
      endAt: props.timeEntry.endAt?.getTime() || Date.now(),
      notes: props.timeEntry.notes,
      isBilled: props.timeEntry.isBilled,
    }
  : {
      projectId: initialProjectId || null,
      userId: timeEntriesFilter.value.userId || user.value?.id || null,
      taskId: initialTaskId || null,
      notes: props.notes || '',
      startAt: props.startAt?.getTime() || Date.now(),
      endAt: props.endAt?.getTime() || props.startAt.getTime() + 3600000,
      isBilled: false,
    })

const { edited, reset } = useFormModel(model)

/* End of form */

/* Start of projects */

const taskListsFilter = computed<ApiTaskListGetListParams & { withTasks: true, withCompleted: false }>(() => {
  return {
    projectId: model.value.projectId ?? 0,
    withTasks: true,
    withCompleted: false,
  }
})

const { data: taskListsData, refresh: fetchTaskLists, status: taskListsFetchStatus } = useApiTaskListGetList(taskListsFilter, { immediate: false, watch: false })

const selectedProject = computed<Project<'withLeads'> | undefined>(() => {
  return props.projects.find(project => project.id === (props.projectId ?? model.value.projectId))
})

async function switchProject() {
  // clear dependent selections on project select
  await fetchTaskLists()
  model.value.taskId = null
  model.value.userId = model.value.userId ?? user.value?.id ?? 0
  blurFormElement()
}

/* End of projects */

/* Start of permissions */

const action: AuthorizationAction = props.timeEntry ? 'update' : 'create'
const { can } = useAppAbility()

const timeEntrySubject = computed(() => {
  return subject('time-entry', {
    ...(props.timeEntry || model.value),
    project: selectedProject.value,
  })
})

/* End of permissions */

/* Start of users */

type UserOption = SelectOption & {
  avatar: string | null
  initials: string
}

const userOptions = computed<UserOption[]>(() => {
  return props.users.map((u) => {
    return {
      label: `${u.forename} ${u.surname}`,
      value: u.id,
      avatar: u.imagePath,
      initials: `${u.forename[0]}${u.surname[0]}`,
    }
  })
})

function renderUserLabel(option: UserOption) {
  return h('div', { class: 'flex items-center space-x-1 py-1' }, [
    h(NAvatar, {
      src: option.avatar || '/placeholder',
      round: true,
      size: 'small',
    }, {
      fallback: () => h('div', { class: 'w-full flex items-center justify-center' }, `${option.initials}`),
    }),
    h('span', {}, { default: () => option.label }),
  ])
}

/* End of users */

/* Start of tasks */

const taskLists = computed<TaskList[]>(() => {
  return taskListsData.value ? taskListsData.value.data : props.taskLists
})

const taskOptions = computed<Array<SelectOption | SelectGroupOption>>(() => {
  return taskLists.value.map((taskList) => {
    return {
      type: 'group',
      label: taskList.name,
      key: `${taskList.projectId}-${taskList.id}`,
      children: taskList.tasks.map((task) => {
        return {
          label: task.name,
          value: task.id,
          key: `${taskList.projectId}-${taskList.id}-${task.id}`,
        }
      }),
    }
  })
})

const selectedTask = computed<Task<undefined> | undefined>(() => {
  for (const taskList of taskLists.value) {
    const task = taskList.tasks.find(task => task.id === model.value.taskId)
    if (task)
      return task
  }
  return undefined
})

/* End of tasks */

/* Start of submit */

const timeEntryActions = useTimeEntryActionsStore()

const {
  timeEntryIdToUpdate,
  timeEntryUpdateParams,
  timeEntryUpdateData,
  timeEntryToCreate,
  timeEntryCreateData,
  timeEntryCreateError,
  timeEntryUpdateError,
  isTimeEntryCreateLoading,
  isAnyTimeEntryUpdateLoading,
  timeEntryIdToDelete,
  timeEntryDeleteError,
  isAnyTimeEntryDeleteLoading,
} = storeToRefs(timeEntryActions)

const { formRef, rules, onSubmit, apiErrors } = useNaiveForm(model)
const { apiErrorMessages, clearApiErrors } = useApiErrors<ApiTimeEntryCreateParams>(apiErrors, timeEntryCreateError, timeEntryUpdateError)

rules.value = {
  projectId: [
    {
      required: true,
      message: 'Projekt auswählen',
      trigger: ['blur', 'input'],
      type: 'number',
    },
  ],
  taskId: [
    {
      key: 'taskId',
      validator: () => !apiErrors.value.taskId,
      message: () => apiErrorMessages.value.taskId!,
      trigger: ['input', 'blur'],
    },
  ],
  startAt: [
    {
      required: true,
      message: 'Startzeit auswählen',
      type: 'number',
      trigger: ['blur', 'input'],
    },
    {
      validator: () => !apiErrors.value.startAt,
      message: () => apiErrorMessages.value.startAt!,
      trigger: ['input', 'blur'],
    },
  ],
  endAt: [
    {
      required: true,
      message: 'Endzeit auswählen',
      type: 'number',
      trigger: ['blur', 'input'],
    },
    {
      validator: (_rule, val) => val > model.value.startAt,
      message: () => 'Der Endzeitpunkt muss nach dem Start liegen',
      trigger: ['blur', 'input'],
    },
    {
      validator: () => !apiErrors.value.endAt,
      message: () => apiErrorMessages.value.endAt!,
    },
  ],
  userId: [
    {
      validator: () => !apiErrors.value.userId,
      message: () => apiErrorMessages.value.userId!,
      trigger: ['input', 'blur'],
    },
  ],
}

const { timeEntries } = storeToRefs(useTimeEntries())

function submitForm() {
  if ((props.timeEntry && !edited.value) || isTimeEntryCreateLoading.value || isAnyTimeEntryUpdateLoading.value)
    return

  clearApiErrors()
  onSubmit(handleSubmit)
}

useMetaSShortcut(submitForm)

async function handleSubmit() {
  if (!props.timeEntry) {
    timeEntryToCreate.value = {
      userId: (model.value.userId || user.value?.id)!,
      startAt: new Date(model.value.startAt),
      endAt: new Date(model.value.endAt),
      taskId: model.value.taskId!,
      notes: model.value.notes,
      isBilled: can('create', timeEntrySubject.value, 'isBilled') ? model.value.isBilled : undefined,
    }

    await timeEntryActions.createTimeEntry()

    if (timeEntryCreateError.value) {
      toast.add({ color: 'error', description: timeEntryCreateError.value?.data?.message ?? unknownError })
    }
    else if (timeEntryCreateData.value) {
      const timeEntry = timeEntryCreateData.value.data
      timeEntries.value = [...timeEntries.value, timeEntry]
      if (activeCalendar.value) {
        const event: CalendarEventInput = {
          timeEntry,
          title: timeEntry.task.name,
          start: timeEntry.startAt,
          end: timeEntry.endAt!,
          id: `${timeEntry.id}`,

        }
        activeCalendar.value.getApi().addEvent(event, timeEntriesEventsSourceId.description)
      }

      lastManualTimeEntry.value = {
        projectId: timeEntry.projectId,
        taskId: timeEntry.taskId,
        durationSec: undefined,
      }
      emit('close', false)
      toast.add({ color: 'success', description: 'Zeiteintrag erfolgreich erstellt!' })
    }
  }
  else {
    timeEntryIdToUpdate.value = props.timeEntry?.id ?? -1

    timeEntryUpdateParams.value = {
      userId: can('update', timeEntrySubject.value, 'userId') ? (model.value.userId || user.value?.id)! : undefined,
      startAt: can('update', timeEntrySubject.value, 'startAt') ? new Date(model.value.startAt) : undefined,
      endAt: can('update', timeEntrySubject.value, 'endAt') ? new Date(model.value.endAt) : undefined,
      taskId: can('update', timeEntrySubject.value, 'taskId') ? model.value.taskId! : undefined,
      notes: can('update', timeEntrySubject.value, 'notes') ? model.value.notes : undefined,
      isBilled: can('update', timeEntrySubject.value, 'isBilled') ? model.value.isBilled : undefined,
    }

    await timeEntryActions.updateTimeEntry()

    if (timeEntryUpdateError.value) {
      toast.add({ color: 'error', description: timeEntryUpdateError.value?.data?.message ?? unknownError })
    }
    else if (timeEntryUpdateData.value) {
      timeEntries.value = [...timeEntries.value.filter(timeEntry => timeEntry.id !== props.timeEntry?.id), timeEntryUpdateData.value.data]
      if (activeCalendar.value) {
        const timeEntry = timeEntryUpdateData.value.data
        activeCalendar.value.getApi().getEventById(`${props.timeEntry?.id}`)?.remove()

        activeCalendar.value.getApi().addEvent({
          timeEntry,
          title: timeEntry.task.name,
          start: timeEntry.startAt,
          end: timeEntry.endAt!,
          id: `${timeEntry.id}`,
        }, timeEntriesEventsSourceId.description)
      }

      lastManualTimeEntry.value = {
        projectId: timeEntryUpdateData.value.data.projectId,
        taskId: timeEntryUpdateData.value.data.taskId,
        durationSec: undefined,
      }

      emit('close', false)
      toast.add({ color: 'success', description: 'Zeiteintrag erfolgreich aktualisiert!' })
    }
  }
}

/* End of submit */

/* Start of task creation */
async function onTaskCreated(task: Task<undefined>) {
  await fetchTaskLists()
  model.value.taskId = task.id
  formRef.value?.validate(undefined, rule => rule.key === 'taskId')
}
/* End of task creation */

/* Start of grid related vars */

const canSelectUserId = computed(() => {
  return (can(action, timeEntrySubject.value, 'userId') || can('read', 'time-entry')) && model.value.projectId != null
})

/* End of grid related vars */

/* Start of delete dialog */

const timeEntryDeleteModal = overlay.create(TimeEntryDeleteModal)

function openTimeEntryDeleteDialog() {
  const timeEntry = props.timeEntry
  if (!timeEntry)
    return

  timeEntryDeleteModal.open({
    timeEntry,
    onClose: async (deleted) => {
      if (deleted) {
        await deleteTimeEntry()
        emit('close', false)
      }
    },
  })
}

async function deleteTimeEntry() {
  timeEntryIdToDelete.value = props.timeEntry?.id ?? -1
  loadingBar.start()
  await timeEntryActions.deleteTimeEntry()
  loadingBar.finish()

  if (!timeEntryDeleteError.value) {
    timeEntryDeleteModal.close()
    toast.add({ color: 'success', description: 'Zeiteintrag erfolgreich gelöscht!' })
    timeEntries.value = timeEntries.value.filter(timeEntry => timeEntry.id !== timeEntryIdToDelete.value)
    if (activeCalendar.value)
      activeCalendar.value.getApi().getEventById(`${timeEntryIdToDelete.value}`)?.remove()
  }
  else {
    toast.add({ color: 'error', description: timeEntryDeleteError.value.data?.message ?? unknownError })
  }
}

/* End of delete dialog */

const submitFocusDiv = ref<HTMLDivElement | null>(null)

function blurFormElement() {
  nextTick(() => submitFocusDiv.value?.focus())
}

const clipboardStore = useClipboardStore()
const {
  task: copiedTask,
  project: copiedProject,
  taskList: copiedTaskList,
} = storeToRefs(clipboardStore)

const isProjectPasted = refAutoReset(false, 1500)
const isTaskPasted = refAutoReset(false, 1500)

async function onPaste(event?: ClipboardEvent) {
  formRef.value?.restoreValidation()

  if (copiedProject.value || copiedTaskList.value) {
    event?.preventDefault()
    event?.stopPropagation()
    const projectId = copiedProject.value?.id ?? copiedTaskList.value?.projectId ?? -1
    if (!props.projects.some(project => project.id === projectId)) {
      toast.add({ color: 'error', title: 'Projekt nicht gefunden!', description: 'Das kopierte Projekt konnte nicht gefunden werden! Ist es bereits abgeschlossen?' })
      return
    }
    model.value.projectId = projectId
    await switchProject()
    isProjectPasted.value = true
  }
  else if (copiedTask.value) {
    event?.preventDefault()
    event?.stopPropagation()
    if (!props.projects.some(project => project.id === copiedTask.value?.taskList.projectId)) {
      toast.add({ color: 'error', title: 'Projekt nicht gefunden!', description: 'Das kopierte Projekt konnte nicht gefunden werden! Ist es bereits abgeschlossen?' })
      return
    }

    model.value.projectId = copiedTask.value.taskList.projectId
    isTaskPasted.value = true

    await switchProject()

    if (!taskLists.value.some(taskList => taskList.id === copiedTask.value?.taskListId && taskList.tasks.some(task => task.id === copiedTask.value?.id))) {
      toast.add({ color: 'error', title: 'Aufgabe nicht gefunden!', description: 'Die kopierte Aufgabe konnte nicht gefunden werden! Ist die Aufgabe oder Aufgabenliste bereits abgeschlossen?' })
      return
    }

    model.value.taskId = copiedTask.value.id
  }
}

const showGlobalSearch = ref(false)

async function onGlobalSearchProjectSelected(project: Project<'withClient'>) {
  model.value.projectId = project.id
  await switchProject()
}

async function onGlobalSearchTaskListSelected(taskList: TaskList<'withProject'>) {
  model.value.projectId = taskList.projectId
  await switchProject()
}

async function onGlobalSearchTaskSelected(task: Task<'withTaskList'>) {
  model.value.projectId = task.taskList.projectId
  await switchProject()
  model.value.taskId = task.id
}
</script>

<template>
  <UModal
    :close="{ onClick: () => $emit('close', false) }"
    :dismissible="false"
    :title="title"
  >
    <template #header>
      <div class="w-full flex justify-between items-center">
        <span>{{ title }}</span>
        <TimesheetsTimeEntryDuration :start-at="model.startAt" :end-at="model.endAt" />
      </div>
      <UButton icon="i-heroicons-x-mark" color="neutral" variant="ghost" @click="$emit('close', false)" />
    </template>
    <template #body>
      <NForm
        ref="formRef"
        :model="model"
        :rules="rules"
        @submit.prevent="submitForm"
        @paste="onPaste"
      >
        <div
          ref="submitFocusDiv"
          class="focus:border-none focus:outline-none"
          tabindex="0"
          @keydown.enter="submitForm"
        >
          <div class="grid auto-rows-max grid-cols-1 gap-x-6 md:grid-cols-[250px_1fr]">
            <div class="grid row-span-3 grid-rows-subgrid items-start">
              <NFormItem label="Projekt" path="projectId" required>
                <NInputGroup class="w-full">
                  <ProjectSelect
                    v-model="model.projectId"
                    :projects="projects"
                    show-recent-projects-group
                    :disabled="!can(action, timeEntrySubject, 'projectId')"
                    @update:value="switchProject"
                    @keydown.enter.stop
                  />
                  <NTooltip v-if="copiedProject || copiedTaskList?.project">
                    <template #trigger>
                      <NButton @click="() => onPaste()">
                        <template #icon>
                          <UIcon :name="isProjectPasted ? 'i-ic-outline-fact-check' : 'i-ic-baseline-content-paste-go'" />
                        </template>
                      </NButton>
                    </template>
                    <span>Projekt "{{ (copiedProject ?? copiedTaskList?.project)!.name }}" aus Zwischenablage übernehmen</span>
                  </NTooltip>
                </NInputGroup>
              </NFormItem>
              <NFormItem label="Aufgabe" path="taskId" required>
                <div class="flex flex-col space-y-5 w-full">
                  <NInputGroup class="w-full">
                    <TaskSelect
                      v-model:value="model.taskId"
                      :project-id="model.projectId"
                      :project="selectedProject"
                      :task-lists="taskLists"
                      :select-props="{
                        disabled: !model.projectId || !can(action, timeEntrySubject, 'taskId'),
                        loading: taskListsFetchStatus === 'pending',
                      }"
                      :show-task-add-action="model.projectId !== null"
                      @task-created="onTaskCreated"
                      @keydown.enter.stop
                      @update:value="() => blurFormElement()"
                    />
                    <NTooltip v-if="copiedTask">
                      <template #trigger>
                        <NButton @click="() => onPaste()">
                          <template #icon>
                            <UIcon :name="isTaskPasted ? 'i-ic-outline-fact-check' : 'i-ic-baseline-content-paste-go'" />
                          </template>
                        </NButton>
                      </template>
                      <span>Aufgabe "{{ copiedTask.name }}" aus Zwischenablage übernehmen</span>
                    </NTooltip>
                  </NInputGroup>
                  <GlobalSearch
                    v-model:show="showGlobalSearch"
                    select-mode
                    :show-reports="false"
                    :show-clients="false"
                    :disable-shortcut-in-modals="false"
                    :include-completed-and-archived-resources="false"
                    @project-selected="onGlobalSearchProjectSelected"
                    @task-list-selected="onGlobalSearchTaskListSelected"
                    @task-selected="onGlobalSearchTaskSelected"
                  >
                    <template #trigger>
                      <NTooltip>
                        <template #trigger>
                          <NButton type="default" secondary size="tiny" @click="showGlobalSearch = true">
                            Projekt/Aufgabe suchen
                            <template #icon>
                              <UIcon name="i-ic-baseline-search" />
                            </template>
                          </NButton>
                        </template>
                        Tippe <UKbd>/</UKbd>, um direkt nach Aufgaben/Aufgabenlisten oder Projekten zu suchen
                      </NTooltip>
                    </template>
                  </GlobalSearch>
                </div>
              </NFormItem>
              <NFormItem label="Mitarbeiter:in" path="userId">
                <NSelect
                  v-model:value="model.userId"
                  :render-label="renderUserLabel"
                  :options="userOptions"
                  filterable
                  :disabled="!canSelectUserId || !model.projectId || !can(action, timeEntrySubject, 'userId')"
                  placeholder="Mitarbeiter:in auswählen"
                  @keydown.enter.stop
                  @update:value="() => blurFormElement()"
                />
              </NFormItem>
            </div>
            <div class="grid row-span-3 grid-rows-subgrid items-start">
              <div class="flex items-start space-x-4">
                <NFormItem label="Startzeit" path="startAt" required class="grow">
                  <NDatePicker
                    v-model:value="model.startAt"
                    class="w-full"
                    type="datetime"
                    update-value-on-close
                    :disabled="!can(action, timeEntrySubject, 'startAt')"
                    @keydown.enter.stop
                    @confirm="() => blurFormElement()"
                  />
                </NFormItem>
                <NFormItem label="Endzeit" path="endAt" required class="grow">
                  <NDatePicker
                    v-model:value="model.endAt"
                    class="w-full"
                    type="datetime"
                    update-value-on-close
                    :disabled="!can(action, timeEntrySubject, 'endAt')"
                    @keydown.enter.stop
                    @confirm="() => blurFormElement()"
                  />
                </NFormItem>
              </div>
              <NFormItem label="Anmerkungen" path="notes">
                <NInput
                  v-model:value="model.notes"
                  type="textarea"
                  :options="taskOptions"
                  placeholder="Optionale Anmerkungen hinzufügen"
                  class="h-full"
                  :resizable="false"
                  filterable
                  :disabled="!can(action, timeEntrySubject, 'notes')"
                  @keydown.enter.stop
                />
              </NFormItem>
              <NFormItem
                v-if="can(action, timeEntrySubject, 'isBilled')
                  && (selectedProject?.billingType === 'times_and_material')
                  && selectedTask?.isBillable" label="Abgerechnet" path="isBilled"
              >
                <NSwitch
                  v-model:value="model.isBilled"
                  size="large"
                  :rail-style="railStyle"
                  @keydown.enter.stop
                >
                  <template #checked>
                    Abgerechnet
                  </template>
                  <template #unchecked>
                    Nicht abgerechnet
                  </template>
                </NSwitch>
              </NFormItem>
            </div>
          </div>
          <div class="flex justify-between -my-1 sm:my-0 flex-wrap gap-2 items-center">
            <TimeTrackerButton
              v-if="timeEntry"
              :button-props="{ tertiary: true, disabled: !model.taskId }"
              :task-id="model.taskId ?? timeEntry.taskId"
            />
            <div v-else />
            <NButtonGroup class="flex-wrap sm:flex-nowrap">
              <NTooltip>
                <template #trigger>
                  <NButton
                    attr-type="submit"
                    class="my-1 sm:my-0"
                    type="primary"
                    :disabled="(timeEntry && !edited) || isAnyTimeEntryUpdateLoading || isTimeEntryCreateLoading"
                    :loading="isAnyTimeEntryUpdateLoading || isTimeEntryCreateLoading"
                  >
                    {{ timeEntry ? 'Aktualisieren' : 'Eintrag hinzufügen' }}
                  </NButton>
                </template>
                <ShortcutDisplaySave />
              </NTooltip>
              <NButton
                class="my-1 sm:my-0"
                secondary
                :disabled="(timeEntry && !edited) || isAnyTimeEntryUpdateLoading || isTimeEntryCreateLoading"
                :loading="isAnyTimeEntryUpdateLoading || isTimeEntryCreateLoading"
                @click="() => timeEntry ? reset() : $emit('close', false)"
              >
                {{ timeEntry ? 'Zurücksetzen' : 'Abbrechen' }}
              </NButton>
              <NButton
                v-if="timeEntry && can('delete', subject('time-entry', timeEntry))"
                class="my-1 sm:my-0"
                :loading="isAnyTimeEntryDeleteLoading"
                secondary
                type="error"
                @click="openTimeEntryDeleteDialog"
              >
                Löschen
              </NButton>
            </NButtonGroup>
          </div>
        </div>
      </NForm>
    </template>
  </UModal>
</template>
